import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  Activity, Loader2, Search, RefreshCw, CheckCircle2, XCircle, AlertTriangle, Info,
  LogIn, LogOut, UserPlus, Shield, Calendar, Building2, CreditCard, Settings,
  ChevronLeft, ChevronRight, Filter
} from "lucide-react";

const categoryIcons = {
  AUTH: LogIn,
  USER: UserPlus,
  BOOKING: Calendar,
  VENUE: Building2,
  PAYMENT: CreditCard,
  ADMIN: Shield,
  SYSTEM: Settings,
};

const categoryColors = {
  AUTH: "bg-blue-100 text-blue-800",
  USER: "bg-purple-100 text-purple-800",
  BOOKING: "bg-green-100 text-green-800",
  VENUE: "bg-amber-100 text-amber-800",
  PAYMENT: "bg-pink-100 text-pink-800",
  ADMIN: "bg-red-100 text-red-800",
  SYSTEM: "bg-slate-100 text-slate-800",
};

const statusIcons = {
  success: CheckCircle2,
  failure: XCircle,
  warning: AlertTriangle,
  info: Info,
};

const statusColors = {
  success: "text-green-600 bg-green-50",
  failure: "text-red-600 bg-red-50",
  warning: "text-amber-600 bg-amber-50",
  info: "text-blue-600 bg-blue-50",
};

export default function PlatformLogs() {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("ALL");
  const [status, setStatus] = useState("all");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const limit = 50;

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = { limit, skip: page * limit };
      if (category !== "ALL") params.category = category;
      if (status !== "all") params.status = status;
      if (query) params.q = query;
      
      const res = await api.get("/admin/platform-logs/combined", { params });
      setLogs(res.data.logs || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      console.error("Failed to fetch logs:", err);
      setLogs([]);
    }
    setLoading(false);
  };

  const fetchStats = async () => {
    try {
      const res = await api.get("/admin/platform-logs/stats");
      setStats(res.data);
    } catch (err) {
      console.error("Failed to fetch stats:", err);
    }
  };

  useEffect(() => {
    fetchLogs();
    fetchStats();
  }, [page, category, status]);

  const handleSearch = () => {
    setPage(0);
    fetchLogs();
  };

  const handleRefresh = () => {
    fetchLogs();
    fetchStats();
  };

  const formatTimestamp = (ts) => {
    if (!ts) return "-";
    const date = new Date(ts);
    return date.toLocaleString("en-US", {
      month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false
    });
  };

  const getActionLabel = (action) => {
    const labels = {
      LOGIN_SUCCESS: "User Login",
      LOGIN_FAILED: "Login Failed",
      LOGIN_BLOCKED: "Login Blocked",
      LOGOUT: "User Logout",
      REGISTER_SUCCESS: "User Registration",
      REGISTER_FAILED: "Registration Failed",
      LOGIN: "Login Attempt",
      REGISTER: "Registration",
      PROFILE_UPDATE: "Profile Updated",
      VENUE_CREATED: "Venue Created",
      BOOKING_CREATED: "Booking Created",
      BOOKING_CANCELLED: "Booking Cancelled",
      DELETE_USER: "User Deleted",
      CHANGE_USER_ROLE: "Role Changed",
      CHANGE_USER_STATUS: "Status Changed",
      CHANGE_PROVIDER_STATUS: "Provider Status Changed",
      OVERRIDE_BOOKING: "Booking Override",
      CHANGE_ROLE: "Role Changed",
      CHANGE_USER_STATUS: "User Status Changed",
      CHANGE_PROVIDER_STATUS: "Provider Status",
      DELETE_USER: "User Deleted",
      PASSWORD_CHANGED: "Password Changed",
      PASSWORD_CHANGE_FAILED: "Password Change Failed",
      ALERT_RESOLVED: "Alert Resolved",
      ALERT_DISMISSED: "Alert Dismissed",
      ALERT_ACKNOWLEDGED: "Alert Acknowledged",
    };
    return labels[action] || action?.replace(/_/g, " ");
  };

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-6 md:py-10" data-testid="platform-logs-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold uppercase tracking-tight flex items-center gap-3">
            <Activity className="w-8 h-8 text-court-orange" />
            Platform Activity Logs
          </h1>
          <p className="text-muted-foreground mt-1">Comprehensive monitoring of all platform activities</p>
        </div>
        <Button onClick={handleRefresh} variant="outline" className="rounded-none border-2 font-heading uppercase" data-testid="refresh-logs-btn">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="border-2">
            <CardHeader className="pb-2">
              <CardTitle className="text-xs font-heading uppercase tracking-wider text-muted-foreground">Total Activities</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">{stats.total?.toLocaleString() || 0}</p>
            </CardContent>
          </Card>
          <Card className="border-2 border-green-200 bg-green-50/50">
            <CardHeader className="pb-2">
              <CardTitle className="text-xs font-heading uppercase tracking-wider text-green-600">Positive Actions</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-green-700">{stats.positive?.toLocaleString() || 0}</p>
            </CardContent>
          </Card>
          <Card className="border-2 border-red-200 bg-red-50/50">
            <CardHeader className="pb-2">
              <CardTitle className="text-xs font-heading uppercase tracking-wider text-red-600">Negative Actions</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-red-700">{stats.negative?.toLocaleString() || 0}</p>
            </CardContent>
          </Card>
          <Card className="border-2 border-amber-200 bg-amber-50/50">
            <CardHeader className="pb-2">
              <CardTitle className="text-xs font-heading uppercase tracking-wider text-amber-600">Failures (24h)</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-amber-700">{stats.last_24h?.failures || 0}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Category Stats */}
      {stats?.categories && (
        <div className="flex flex-wrap gap-2 mb-6">
          {Object.entries(stats.categories).map(([cat, count]) => {
            const Icon = categoryIcons[cat] || Activity;
            return (
              <button
                key={cat}
                onClick={() => setCategory(category === cat ? "ALL" : cat)}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-heading uppercase tracking-wide transition-all ${
                  category === cat 
                    ? "bg-court-orange text-white" 
                    : `${categoryColors[cat]} hover:opacity-80`
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                {cat}: {count}
              </button>
            );
          })}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-3 mb-6">
        <div className="flex-1 flex gap-2">
          <Input 
            data-testid="log-search-input"
            value={query} 
            onChange={e => setQuery(e.target.value)}
            placeholder="Search by action, email..." 
            className="h-11 rounded-none border-2" 
            onKeyDown={e => e.key === "Enter" && handleSearch()} 
          />
          <Button data-testid="log-search-btn" onClick={handleSearch} className="bg-court-orange hover:bg-court-orange-hover text-white rounded-full px-6">
            <Search className="w-4 h-4" />
          </Button>
        </div>
        <Select value={category} onValueChange={v => { setCategory(v); setPage(0); }}>
          <SelectTrigger data-testid="category-filter" className="w-full md:w-40 h-11 rounded-none border-2">
            <SelectValue placeholder="Category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All Categories</SelectItem>
            <SelectItem value="AUTH">Auth</SelectItem>
            <SelectItem value="USER">User</SelectItem>
            <SelectItem value="BOOKING">Booking</SelectItem>
            <SelectItem value="VENUE">Venue</SelectItem>
            <SelectItem value="PAYMENT">Payment</SelectItem>
            <SelectItem value="ADMIN">Admin</SelectItem>
            <SelectItem value="SYSTEM">System</SelectItem>
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={v => { setStatus(v); setPage(0); }}>
          <SelectTrigger data-testid="status-filter" className="w-full md:w-40 h-11 rounded-none border-2">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="positive">✓ Positive</SelectItem>
            <SelectItem value="negative">✗ Negative</SelectItem>
            <SelectItem value="success">Success</SelectItem>
            <SelectItem value="failure">Failure</SelectItem>
            <SelectItem value="warning">Warning</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Results count */}
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-muted-foreground">{total} activities found</p>
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => setPage(Math.max(0, page - 1))}
            disabled={page === 0}
            className="h-8 w-8 p-0 rounded-none border-2"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="text-sm">Page {page + 1}</span>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => setPage(page + 1)}
            disabled={(page + 1) * limit >= total}
            className="h-8 w-8 p-0 rounded-none border-2"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Logs Table */}
      {loading ? (
        <div className="flex justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-court-orange" />
        </div>
      ) : (
        <div className="bg-white border-2 border-slate-100 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="font-heading uppercase tracking-wider text-xs w-[160px]">Timestamp</TableHead>
                <TableHead className="font-heading uppercase tracking-wider text-xs">Status</TableHead>
                <TableHead className="font-heading uppercase tracking-wider text-xs">Action</TableHead>
                <TableHead className="font-heading uppercase tracking-wider text-xs">User</TableHead>
                <TableHead className="font-heading uppercase tracking-wider text-xs">Details</TableHead>
                <TableHead className="font-heading uppercase tracking-wider text-xs">IP</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {logs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-10 text-muted-foreground">
                    No activity logs found
                  </TableCell>
                </TableRow>
              ) : (
                logs.map((log, idx) => {
                  const StatusIcon = statusIcons[log.status] || Info;
                  const CatIcon = categoryIcons[log.category] || Activity;
                  return (
                    <TableRow key={log.log_id || idx} data-testid={`log-row-${idx}`} className={!log.is_positive ? "bg-red-50/30" : ""}>
                      <TableCell className="text-xs text-muted-foreground font-mono whitespace-nowrap">
                        {formatTimestamp(log.timestamp)}
                      </TableCell>
                      <TableCell>
                        <div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded ${statusColors[log.status] || 'bg-slate-100'}`}>
                          <StatusIcon className="w-3.5 h-3.5" />
                          <span className="text-[10px] font-heading uppercase">{log.status}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Badge className={`${categoryColors[log.category] || ''} text-[10px] font-heading uppercase`}>
                            <CatIcon className="w-3 h-3 mr-1" />
                            {log.category}
                          </Badge>
                          <span className="text-sm font-medium">{getActionLabel(log.action)}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="text-sm font-medium">{log.actor?.email || "-"}</p>
                          {log.actor?.role && (
                            <p className="text-[10px] text-muted-foreground">{log.actor.role}</p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="max-w-[200px]">
                        {log.error_message ? (
                          <span className="text-xs text-red-600">{log.error_message}</span>
                        ) : log.details?.detail || log.details?.reason ? (
                          <span className="text-xs text-muted-foreground">{log.details?.detail || log.details?.reason}</span>
                        ) : log.target?.type ? (
                          <span className="text-xs text-muted-foreground">
                            {log.target.type}: {log.target.name || log.target.id || "-"}
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground font-mono">
                        {log.client?.ip || log.ip || "-"}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
